import { useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useAuthStore } from '@indipia/shared/stores/authStore';
import { useAppStore } from '@indipia/shared';
import { SiteRegistrationPopup } from '@indipia/shared-features';
import { Alert, Badge, Button, EmptyState, FileInput, Page, PageScroll, Progress, Steps, Tag } from '@indipia/ui-core';
import { photoApi } from '../api/photoApi';
import type { BatchUploadPhotoItem } from '../api/photoApi';
import { usePhotoNavStore } from '../store/photoNavStore';
import { QrHandoffPanel } from '../components/QrHandoffPanel';
import { SiteSelectModal } from './SiteSelectModal';

const BASE = '/agents/photo-organizer';

type UploadState = 'idle' | 'selected' | 'uploading';
type FileStatus = 'idle' | 'uploading' | 'completing' | 'done';

interface UploadedFile {
  name: string;
  url?: string;
  status: FileStatus;
  size: number;
}

// 3화면(업로드·분류·태그) 공용 단계 라벨 — 표준 컴포넌트 StepsDetail(D-09 §15.22)로 렌더한다.
const UPLOAD_STEP_LABELS = ['사진업로드', '현장분류', '태그 등록'] as const;

// ─── 진입 동선(entry)별 화면 구성 ───────────────────────────────────────────
// 스튜디오 홈 "오늘은 어떤 일부터 시작해볼까요?" 의 각 항목이 ?entry= 를 달고 이 화면으로 들어온다.
// 신규 페이지를 만들지 않고, 같은 화면이 entry 에 따라 다르게 구성된다.
//   MOBILE·CUSTOMER·PARTNER — 폰 갤러리에서 가져오는 동선이라 QR 하나만 보여준다(qr 지정).
//   PC — 컴퓨터에서 가져오는 동선이라 드롭존만 남긴다(dropzone 지정, QR 숨김).
//   (entry 없음) — LNB "사진" 메뉴 직접 진입: 드롭존 + QR 2단 현행 구성.
// 칼럼 최대 폭은 로컬 상수를 두지 않는다 — <PageScroll width="lg"> 가 갖는다(D-07 §21.3·§21.4).

interface EntryConfig {
  /** 화면 상단 안내 배너. icon 미지정 시 카메라. */
  banner: { title: string; desc: React.ReactNode; icon?: 'camera' | 'info' };
  /** QR 패널 문구 — 지정하면 QR 카드만 보여준다(드롭존 숨김) */
  qr?: { title: string; subtitle: string; description: string; expiredLabel: string };
  /** 드롭존 상단 문구 — 지정하면 드롭존만 보여준다(QR 숨김) */
  dropzone?: { title: string; subtitle: string };
  /** 단계 서브라벨 override — 동선에 따라 실제로 하는 일이 달라진다(미지정 시 기본 서브라벨) */
  stepSubs?: readonly [string, string, string];
}

// 휴대폰 갤러리에서 가져오는 동선의 공통부 — QR을 찍으면 폰에서 앱이 열리고 갤러리 피커가 바로 뜬다.
// 사진 출처(내가 찍음 / 고객에게 받음 / 시공반장에게 받음)만 다르고 화면 구성과 이후 단계는 같으므로
// 배너 제목만 갈아끼운다. stepSubs 는 1단계만 동선이 다르고 이후는 동일하다.
const PHONE_GALLERY_ENTRY = {
  bannerDesc: '업로드할 사진을 휴대폰 갤러리에 다운받아 두세요.',
  qr: {
    title: '휴대폰 갤러리 사진 가지러가기',
    subtitle: '인식 후 휴대폰 화면을 확인하세요.',
    description: '휴대폰의 카메라로 QR 코드를 인식해주세요.',
    expiredLabel: 'QR 코드가 만료됐어요',
  },
  stepSubs: ['휴대폰 갤러리에서 선택', '현장 배정', '공간·공종 태깅'],
} as const;

const ENTRY_CONFIG: Record<string, EntryConfig> = {
  // ① 내가 찍은 현장 사진 정리해요
  MOBILE: {
    banner: { title: '내가 찍은 현장사진 정리해요', desc: PHONE_GALLERY_ENTRY.bannerDesc },
    qr: PHONE_GALLERY_ENTRY.qr,
    stepSubs: PHONE_GALLERY_ENTRY.stepSubs,
  },
  // ② 고객에게 받은 사진 정리할까요
  CUSTOMER: {
    banner: { title: '고객에게 받은 사진 정리할까요', desc: PHONE_GALLERY_ENTRY.bannerDesc },
    qr: PHONE_GALLERY_ENTRY.qr,
    stepSubs: PHONE_GALLERY_ENTRY.stepSubs,
  },
  // ③ 시공반장에게 받은 사진도 정리할까요
  PARTNER: {
    banner: { title: '시공반장에게 받은 사진도 정리할까요', desc: PHONE_GALLERY_ENTRY.bannerDesc },
    qr: PHONE_GALLERY_ENTRY.qr,
    stepSubs: PHONE_GALLERY_ENTRY.stepSubs,
  },
  // ④ 컴퓨터에 저장된 사진도 정리해봐요 — PC에서 바로 올리는 동선이라 QR 없이 드롭존만 남긴다.
  PC: {
    banner: {
      title: '컴퓨터에 저장된 사진도 정리해봐요',
      // lg(1060) 칼럼에서 한 줄에 들어간다(실측 743px / 가용 996px) — 강제 개행을 넣지 않는다.
      desc: '파일 탐색기를 열고 파일을 아래 박스로 드래그앤드롭 하거나 [파일 탐색기에서 선택] 클릭 → 복수 파일 선택 → [열기]',
      icon: 'info',
    },
    dropzone: { title: '컴퓨터 사진 가져오기', subtitle: '(복수의 사진 한번에 선택가능)' },
  },
};

// 진입 동선 안내 배너의 카메라 아이콘. info 동선은 Alert 기본 아이콘(ⓘ)을 쓴다.
// 색은 `.alert-info .ai` 가 currentColor 로 준다(D-07 §20.4).
const CAMERA_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const UPLOAD_STEP_SUBS = ['사진 선택', '현장 배정', '공간·공종 태깅'] as const;

// entry 없는 진입(LNB "사진" 메뉴)의 드롭존 제목·부제 — 우측에 QR 패널이 함께 있어 "컴퓨터"임을 밝힌다.
const DEFAULT_DROPZONE_HEAD = { title: '컴퓨터 사진 가져오기', subtitle: '(복수의 사진 한번에 선택가능)' } as const;

// 상하 여백·가운데 정렬은 컴포넌트의 .steps-wrap 이 갖는다(D-09 §15.22).
// 좌우 패딩은 아래 PageScroll 컬럼과 같은 --page-padding-desktop 을 써서 기준선을 맞춘다.
function StepBar({ step, subs = UPLOAD_STEP_SUBS }: {
  step: number;
  subs?: readonly [string, string, string];
}) {
  const steps = [
    { label: UPLOAD_STEP_LABELS[0], sub: subs[0] },
    { label: UPLOAD_STEP_LABELS[1], sub: subs[1] },
    { label: UPLOAD_STEP_LABELS[2], sub: subs[2] },
  ];
  return (
    <div style={{ padding: '0 var(--page-padding-desktop)', flexShrink: 0 }}>
      <Steps steps={steps} current={step - 1} />
    </div>
  );
}

export function UploadMain() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const entryConfig = ENTRY_CONFIG[searchParams.get('entry') ?? ''] ?? null;
  const setUploadResult = usePhotoNavStore(s => s.setUploadResult);
  const setTagBulkEditSavedTags = usePhotoNavStore(s => s.setTagBulkEditSavedTags);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  // 현장 미리 선택 — 지정하면 PC 드롭존/파일선택 배치 전체가 GPS 매칭 없이 이 현장으로 바로 배정됨
  // (QR은 완전 핸드오프라 폰으로 값이 전달되지 않는다 — 폰은 GPS 자동분류/폰 분류화면에서 배정)
  const [selectedSite, setSelectedSite] = useState<{ siteId: number; siteName: string; address: string | null } | null>(null);
  const [siteSelectOpen, setSiteSelectOpen] = useState(false);
  const [siteRegOpen, setSiteRegOpen] = useState(false);
  const [dupConfirm, setDupConfirm] = useState<{
    matchedCount: number;
    unmatchedCount: number;
    matchedSites: string[];
    matchedFiles: File[];
    unmatchedFiles: File[];
    hashGroups: { hash: string; files: File[]; siteName: string | null }[];
  } | null>(null);
  const [overwriteChoice, setOverwriteChoice] = useState<Record<string, number>>({});
  const [isChecking, setIsChecking] = useState(false);
  const rawFilesRef = useRef<File[]>([]);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  const ALLOWED_MIME = new Set([
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/heif',
    'image/webp',
  ]);
  const ALLOWED_EXT = new Set([
    '.jpg',
    '.jpeg',
    '.png',
    '.heic',
    '.heif',
    '.webp',
  ]);
  const MAX_FILE_BYTES = 20 * 1024 * 1024;
  const MAX_BATCH_COUNT = 15;

  const isAllowedFile = (f: File) => {
    if (ALLOWED_MIME.has(f.type)) return true;
    const ext = f.name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? '';
    return ALLOWED_EXT.has(ext);
  };

  const processFiles = useCallback((selected: File[]) => {
    const typed = selected.filter(isAllowedFile);
    const rejected = selected.filter(f => !isAllowedFile(f));
    if (rejected.length) {
      const preview = rejected.slice(0, 3).map(f => f.name).join(', ');
      const suffix = rejected.length > 3 ? ` 외 ${rejected.length - 3}건` : '';
      toast.error(`허용되지 않는 확장자입니다: ${preview}${suffix}`);
    }
    if (!typed.length) return;

    const oversized = typed.filter(f => f.size > MAX_FILE_BYTES);
    if (oversized.length) {
      const preview = oversized.slice(0, 3).map(f => f.name).join(', ');
      const suffix = oversized.length > 3 ? ` 외 ${oversized.length - 3}건` : '';
      toast.error(`20MB 초과 파일은 업로드할 수 없습니다: ${preview}${suffix}`);
    }
    const imgs = typed.filter(f => f.size <= MAX_FILE_BYTES);
    if (!imgs.length) return;

    const existingNames = new Set(rawFilesRef.current.map(f => f.name));
    const duplicates = imgs.filter(f => existingNames.has(f.name));
    let newImgs = imgs.filter(f => !existingNames.has(f.name));

    if (duplicates.length) {
      const preview = duplicates.slice(0, 3).map(f => f.name).join(', ');
      const suffix = duplicates.length > 3 ? ` 외 ${duplicates.length - 3}건` : '';
      toast.error(`이미 추가된 파일입니다: ${preview}${suffix}`);
    }
    if (!newImgs.length) return;

    const remaining = MAX_BATCH_COUNT - rawFilesRef.current.length;
    if (remaining <= 0) {
      toast.error(`최대 ${MAX_BATCH_COUNT}장까지 업로드할 수 있습니다`);
      return;
    }
    if (newImgs.length > remaining) {
      toast.error(`최대 ${MAX_BATCH_COUNT}장까지 업로드할 수 있습니다. ${remaining}장만 추가됩니다`);
      newImgs = newImgs.slice(0, remaining);
    }

    const offset = rawFilesRef.current.length;
    rawFilesRef.current = [...rawFilesRef.current, ...newImgs];
    setFiles(prev => [...prev, ...newImgs.map(f => ({ name: f.name, url: URL.createObjectURL(f), status: 'idle' as FileStatus, size: f.size }))]);
    setUploadState('selected');

    // 파일별로 idle → uploading → completing → done 애니메이션 (API 없음)
    newImgs.forEach((_, i) => {
      const idx = offset + i;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setFiles(prev => prev.map((f, j) => j === idx ? { ...f, status: 'uploading' as FileStatus } : f));
          setTimeout(() => {
            setFiles(prev => prev.map((f, j) => j === idx ? { ...f, status: 'completing' as FileStatus } : f));
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setTimeout(() => {
                  setFiles(prev => prev.map((f, j) => j === idx ? { ...f, status: 'done' as FileStatus } : f));
                }, 600);
              });
            });
          }, 500);
        });
      });
    });
  }, []);

  const removeFile = useCallback((idx: number) => {
    rawFilesRef.current = rawFilesRef.current.filter((_, i) => i !== idx);
    setFiles(prev => prev.filter((_, i) => i !== idx));
    if (rawFilesRef.current.length === 0) setUploadState('idle');
  }, []);

  // ── 1·3·4 실제 업로드 + 분류 처리 ──
  const proceedUpload = async (imgs: File[], overwrite = false) => {
    const user = useAuthStore.getState().user;
    if (!user) { toast.error('인증 정보가 없습니다'); return; }

    setUploadState('uploading');

    // 배치 업로드 — 세션은 서버가 업로드 안에서 생성해 응답에 실어 준다(U3, 사전 세션생성 제거)
    let photoIds: number[];
    let batchPhotos: BatchUploadPhotoItem[] = [];
    let sessionId: number | null = null;
    let failures: { fileName: string; reason: string | null }[] = [];
    try {
      const { data } = await photoApi.uploadBatch(imgs, { overwrite: overwrite || undefined, siteId: selectedSite?.siteId });
      batchPhotos = data.data.photos;
      sessionId = data.data.sessionId;
      failures = data.data.failures ?? [];
      photoIds = batchPhotos.map(p => p.photoId);

      // 덮어쓴 사진(photoId 재사용)은 기존 태그가 이미 붙어있을 수 있다 — 이번 배치 응답의
      // overwrittenTags로 태그 화면(tag-bulk-edit) 진입 전에 미리 채워둔다. 신규 업로드분도
      // 빈 배열로 같이 채워, 태그 화면이 photoId별 getPhotoDetail 재조회를 하지 않게 한다
      // (TagBulkEditMain은 savedTagsAtMount에 없는(undefined) photoId만 개별 조회하는 폴백을 갖고 있음).
      // overwrittenTags 필드 자체가 없으면(BE 배포 전 과도기 등) 아예 손대지 않는다 — 잘못
      // 채우면 모든 photoId가 "확인됨(빈 배열)"으로 잡혀 TagBulkEditMain의 폴백 조회가
      // 막히고, 실제로 태그가 있던 덮어쓴 사진도 복원이 안 되는 역효과가 난다.
      const overwrittenTags = data.data.overwrittenTags;
      if (overwrittenTags) {
        const savedTags: Record<number, string[]> = {};
        batchPhotos.forEach(p => {
          savedTags[p.photoId] = (overwrittenTags[String(p.photoId)] ?? []).map(t => t.tagValue);
        });
        setTagBulkEditSavedTags(savedTags);
      }
    } catch {
      toast.error('사진 업로드에 실패했습니다');
      setUploadState('selected');
      return;
    }

    // 부분 실패 — 실패한 장만 업로드 목록에 남겨 재시도시키고, 성공분은 백그라운드 분류로 마무리한다.
    if (failures.length > 0) {
      const failedNames = new Set(failures.map(f => f.fileName));
      rawFilesRef.current = rawFilesRef.current.filter(f => failedNames.has(f.name));
      setFiles(prev => prev.filter(f => failedNames.has(f.name)).map(f => ({ ...f, status: 'idle' as FileStatus })));
      if (photoIds.length) photoApi.classifyPhotos(photoIds).catch(() => {});
      setUploadState('selected');
      toast.error(
        photoIds.length
          ? `${photoIds.length}장 완료, ${failures.length}장 실패 — 실패한 사진만 다시 올려 주세요`
          : `${failures.length}장 모두 업로드에 실패했어요. 다시 시도해 주세요`,
      );
      return;
    }

    if (!photoIds.length || sessionId == null) {
      toast.error('업로드된 사진이 없습니다');
      setUploadState('selected');
      return;
    }

    // 4. GPS 현장 분류
    let classifyResult;
    try {
      const { data } = await photoApi.classifyPhotos(photoIds);
      classifyResult = data.data;
    } catch {
      toast.error('현장 분류에 실패했습니다');
      setUploadState('selected');
      return;
    }

    // GPS 자동분류 결과 안내 — 매칭된 현장이 1곳이면 현장명을, 여러 곳이면 현장 수를 알려준다.
    // 미매칭(0곳)이면 조용히 넘어간다(분류화면에서 미분류로 바로 보임).
    if (classifyResult.matchedCount > 0) {
      const matched = classifyResult.results.filter(r => r.matchedSiteId != null);
      const distinctSiteIds = new Set(matched.map(r => r.matchedSiteId));
      if (distinctSiteIds.size === 1) {
        const siteName = matched[0]?.siteName ?? '현장';
        toast.success(`${classifyResult.matchedCount}장의 사진이 ${siteName} 현장에 분류되었습니다`);
      } else {
        toast.success(`${classifyResult.matchedCount}장의 사진이 ${distinctSiteIds.size}개의 현장에 분류되었습니다`);
      }
    }

    setUploadResult(classifyResult, sessionId, batchPhotos);
    navigate(`${BASE}/classify-site`, { state: { fromUpload: true } });
  };

  // ── 2. 중복 확인 → 사용자 확인 → proceedUpload ──
  const sha256Hex = async (file: File): Promise<string> => {
    const buf = await file.arrayBuffer();
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleClassify = async () => {
    if (isChecking) return;
    const user = useAuthStore.getState().user;
    if (!user) { toast.error('인증 정보가 없습니다'); return; }

    const imgs = rawFilesRef.current;
    if (!imgs.length) { toast.error('사진을 선택해주세요'); return; }

    const totalBytes = imgs.reduce((sum, f) => sum + f.size, 0);
    if (totalBytes > 50 * 1024 * 1024) {
      const totalMB = (totalBytes / 1024 / 1024).toFixed(1);
      toast.error(`전체 파일 용량(${totalMB}MB)이 50MB를 초과했습니다. 사진을 줄여주세요`);
      return;
    }

    setIsChecking(true);
    try {
      const fileHashes = await Promise.all(imgs.map(sha256Hex));
      const { data } = await photoApi.checkDuplicates(fileHashes);
      const { matchedCount, unmatchedCount, results } = data.data;

      if (matchedCount > 0) {
        const matchedSites = [...new Set(
          results.map(r => r.siteName).filter((s): s is string => s !== null),
        )];
        const matchedFiles = imgs.filter((_, i) => results[i]?.photoId !== null);
        const unmatchedFiles = imgs.filter((_, i) => results[i]?.photoId === null);

        // 선택한 파일들 중 해시가 같아 동일한 기존 사진을 가리키는 그룹을 찾는다
        const hashToIndices = new Map<string, number[]>();
        fileHashes.forEach((h, i) => {
          if (results[i]?.photoId === null) return; // 매칭 안 된 파일은 제외
          const arr = hashToIndices.get(h) ?? [];
          arr.push(i);
          hashToIndices.set(h, arr);
        });
        const hashGroups: { hash: string; files: File[]; siteName: string | null }[] = [];
        hashToIndices.forEach((indices, hash) => {
          if (indices.length > 1) {
            hashGroups.push({
              hash,
              files: indices.map(i => imgs[i]),
              siteName: results[indices[0]]?.siteName ?? null,
            });
          }
        });
        setOverwriteChoice(Object.fromEntries(hashGroups.map(g => [g.hash, 0])));

        setDupConfirm({ matchedCount, unmatchedCount, matchedSites, matchedFiles, unmatchedFiles, hashGroups });
        return; // 사용자 응답 대기
      }
    } catch {
      // 중복 확인 실패 시 무시하고 계속 진행
    } finally {
      setIsChecking(false);
    }

    await proceedUpload(imgs);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    processFiles(Array.from(e.dataTransfer.files));
  };

  // 현장 미리 선택 — 이번 배치는 한 현장에만 배정하므로 여러 곳을 골라도 첫 번째만 사용한다
  const handleSiteConfirm = (sites: { siteId: number; siteName: string; address: string | null }[]) => {
    const site = sites[0];
    if (site) setSelectedSite(site);
    setSiteSelectOpen(false);
  };

  const handleNewSiteComplete = async (siteId: number) => {
    setSiteRegOpen(false);
    try {
      const res = await photoApi.getSiteDetail(siteId);
      setSelectedSite({ siteId, siteName: res.data.data.siteName, address: res.data.data.address });
    } catch {
      setSelectedSite({ siteId, siteName: `현장 #${siteId}`, address: null });
    }
  };

  const isUploading = uploadState === 'uploading';
  const isBusy = isChecking || isUploading;
  const allFilesDone = files.length > 0 && files.every(f => f.status === 'done');

  const totalSizeBytes = files.reduce((sum, f) => sum + f.size, 0);
  const totalSizeMB = (totalSizeBytes / 1024 / 1024).toFixed(1);
  const totalSizeOver = totalSizeBytes > 50 * 1024 * 1024;

  const closeDupConfirm = () => { setDupConfirm(null); setOverwriteChoice({}); };
  const fileThumbUrl = (f: File) => {
    const idx = rawFilesRef.current.indexOf(f);
    return idx >= 0 ? files[idx]?.url : undefined;
  };

  // ── 렌더 조각 ────────────────────────────────────────────────────────────
  // PC 동선과 entry 없는 진입이 **같은 마크업**을 쓴다 — 펼침 상태의 차이는 없다.
  // (구 `inset` 개념은 폐기했다: PC entry 섹션에 감싸는 카드가 없어 바깥 여백·모서리 축소가 잔재였다)

  // row=true 는 우측 QR 카드와 한 행을 이루는 2단 구성(entry 없음 · LNB "사진" 진입)이다.
  // 단일 컬럼(PC entry)에서만 파일이 담기면 한 줄로 접는다(§15.17 compact) — 안내는 이미 읽었고 그 아래 목록이 주인공이 된다.
  // 2단 구성에서 접지 않는 이유는 표준 금지 조건 그대로다 — 행 높이를 형제(QR 카드)가 정하므로
  // 한쪽만 접히면 형제 높이만큼의 빈 박스 안에 한 줄이 떠 있는 모양이 된다.
  const renderDropzone = ({ row = false }: { row?: boolean } = {}) => {
    const compact = !row && files.length > 0;
    const head = entryConfig?.dropzone ?? DEFAULT_DROPZONE_HEAD;
    // 업로드 화살표 — 자리마다 크기가 다르다(칩 48 / compact 18). 공용 칩 안에서는 currentColor 로 색을 받고,
    // 색을 주지 않는 inset 칩에서는 호출부가 stroke 를 직접 넘긴다.
    // strokeWidth 는 viewBox 24 기준이라 렌더 굵기가 size 에 비례한다 — 48 에서는 1.5(=3px 렌더)로 낮춰
    // 공용 아이콘(icons.tsx, viewBox 48 · strokeWidth 3)과 획을 맞춘다.
    const uploadIcon = (size: number, stroke = 'currentColor', strokeWidth = 1.8) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
      </svg>
    );
    const fileInput = (
      <div onClick={e => e.stopPropagation()}>
        <FileInput
          accept=".jpg,.jpeg,.png,.heic,.heif,.webp,image/jpeg,image/png,image/heic,image/heif,image/webp"
          multiple
          size="sm"
          disabled={isUploading}
          onFiles={processFiles}
        >
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            파일 탐색기에서 선택
          </>
        </FileInput>
      </div>
    );
    // 드래그·클릭 배선은 두 경로가 공유한다
    const dropHandlers = {
      onDragOver: (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); },
      onDragLeave: () => setIsDragOver(false),
      onDrop: handleDrop,
      onClick: () => { if (!isUploading) dropzoneRef.current?.querySelector<HTMLInputElement>('input[type=file]')?.click(); },
    };

    // 셸(패딩 32·갭 24·모서리·보더·배경·드래그오버 색)은 전부 공용 클래스가 갖는다(D-09 §15.17).
    // 인라인으로 남기는 것은 이 화면 고유의 2건뿐이다.
    const triggerStyle: React.CSSProperties = {
      // 공용 .is-dragover 는 보더·배경만 바꾼다 — 강조 링만 인라인으로 남긴다
      boxShadow: isDragOver ? '0 0 0 4px color-mix(in srgb, var(--gt-primary) 10%, transparent)' : 'none',
      // .is-disabled 를 쓰지 않는 이유: opacity .6 이 함께 걸려 업로드 중 화면 전체가 흐려진다
      cursor: isUploading ? 'not-allowed' : 'pointer',
    };

    // 접힘 — 칩·안내 한 줄·파일 선택 버튼 3슬롯. 헤더 그룹과 힌트는 노출하지 않는다(§15.17 compact).
    if (compact) {
      return (
        <div ref={dropzoneRef} className={`dropzone is-compact${isDragOver ? ' is-dragover' : ''}`}>
          <div className="dropzone-trigger" {...dropHandlers} style={triggerStyle}>
            <span className="dropzone-chip">{uploadIcon(18)}</span>
            <div className="dropzone-desc">사진을 더 추가하려면 여기로 드래그하세요</div>
            {fileInput}
          </div>
        </div>
      );
    }

    // 펼침 — entry 유무와 무관하게 같은 마크업이다.
    // flex:1 은 2단 구성에서만 붙인다: 우측 QR 카드와 행 높이를 함께 만든다.
    // 단일 컬럼(PC entry)의 부모는 세로 flex 라 flex:1 이 높이 확장으로 잘못 해석된다.
    return (
      <div ref={dropzoneRef} className={`dropzone${isDragOver ? ' is-dragover' : ''}`} style={row ? { flex: 1 } : undefined}>
        <div className="dropzone-trigger" {...dropHandlers} style={row ? { ...triggerStyle, flex: 1 } : triggerStyle}>
          {/* 헤더 그룹 — entry 동선은 ENTRY_CONFIG 값, entry 없는 진입은 DEFAULT_DROPZONE_HEAD */}
          <div className="dropzone-text">
            <div className="dropzone-title">{head.title}</div>
            <div className="dropzone-desc">{head.subtitle}</div>
          </div>
          <span className="dropzone-chip">{uploadIcon(48, 'currentColor', 1.5)}</span>
          {/* 본문 그룹 — 이 제목은 헤더가 아니라 드롭 안내다(크기만 헤더와 같은 18) */}
          <div className="dropzone-text">
            <div className="dropzone-title">사진을 여기로 드래그하세요</div>
            {/* caption 은 행간 100% 라 <br/> 로 접으면 두 줄이 붙는다 — 요소를 나눠 .dropzone-text 의 갭 8 을 쓴다 */}
            <div className="dropzone-hint">JPG · PNG · HEIF · HEIC · WebP</div>
            <div className="dropzone-hint">1장 최대 20MB · 전체 50MB · 최대 15장</div>
          </div>
          {fileInput}
        </div>
      </div>
    );
  };

  /** 섹션 헤더 — 섹션 제목 · 상태 뱃지 · 건수/용량 태그 · 분류하기 CTA (D-09 §15.5 뱃지=상태 / 태그=분류·정보) */
  const renderCardHeader = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
        {/* 제목이 뱃지 앞에 와야 뒤따르는 뱃지·태그가 "무엇의 상태·수치"인지 붙여 읽힌다 */}
        <span className="section-title">업로드 현황</span>
        <Badge status={isUploading ? 'success' : uploadState === 'selected' ? 'info' : 'warning'}>
          {isUploading ? '업로드 중' : uploadState === 'selected' ? '선택됨' : '대기 중'}
        </Badge>
        <Tag>{files.length}/15장</Tag>
        {/* 용량 초과는 "분류"가 아니라 "상태"다 — 뉴트럴 고정인 Tag 로는 경고를 표현할 수 없어 Badge 로 전환한다 */}
        {totalSizeOver
          ? <Badge status="danger">{totalSizeMB}MB/50MB</Badge>
          : <Tag>{totalSizeMB}MB/50MB</Tag>}
      </div>
      <Button variant="primary" size="sm" disabled={!allFilesDone} loading={isBusy} onClick={handleClassify} style={{ flexShrink: 0 }}>
        분류하기
        {!isBusy && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>}
      </Button>
    </div>
  );

  /** 선택 파일 썸네일 그리드 — 파일이 있을 때만 호출한다. 박스는 D-09 §15.32·§15.33 컨테이너 규격. */
  const renderThumbGrid = () => (
    <div style={{ margin: 0, padding: 'var(--num-16)', background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 'var(--radius-xl)', display: 'flex', gap: 'var(--num-12)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {files.map((f, i) => {
        const done = f.status === 'done';
        const completing = f.status === 'completing';
        return (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 5, width: 80, position: 'relative' }}>
            {!isUploading && (
              <button
                onClick={() => removeFile(i)}
                aria-label="파일 제거"
                style={{ position: 'absolute', top: -5, right: -5, width: 18, height: 18, borderRadius: 'var(--radius-full)', background: 'var(--color-danger)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, padding: 0, fontFamily: 'inherit' }}
              >
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
            {/* 썸네일 + 완료 오버레이 */}
            <div style={{ position: 'relative', width: 80, height: 80, borderRadius: 'var(--radius-md)', background: 'var(--gt-subtle)', border: `1px solid ${done ? 'var(--color-success)' : 'var(--shell-border)'}`, overflow: 'hidden', flexShrink: 0, transition: 'border-color .5s' }}>
              {f.url
                ? <img src={f.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt={f.name} />
                : <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
              }
              {done && (
                <div style={{ position: 'absolute', inset: 0, background: 'var(--alpha-black-300)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-full)', background: 'var(--color-success)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  </div>
                </div>
              )}
            </div>
            <Progress
              value={done || completing ? 100 : f.status === 'uploading' ? 88 : 0}
              style={{ width: '100%', height: 6 }}
            />
            <span style={{ fontSize: 'var(--type-caption-size)', color: done ? 'var(--color-success)' : 'var(--shell-hint)', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: done ? 600 : 400, transition: 'color .4s' }}>{f.name}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {/* ── 중복 파일 확인 모달 ── */}
      {dupConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="중복 사진 확인"
          style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-overlay)', background: 'var(--alpha-black-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' }}
          onClick={e => { if (e.target === e.currentTarget) closeDupConfirm(); }}
        >
          <style>{`[data-dup-modal]::-webkit-scrollbar { display: none; }`}</style>
          <div data-dup-modal style={{ background: 'var(--shell-surface)', borderRadius: 'var(--radius-xl)', padding: '28px 28px 24px', width: 'min(380px, 92vw)', maxHeight: '90vh', overflowY: 'auto', scrollbarWidth: 'none', boxShadow: '0 24px 64px var(--alpha-black-300)', display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* 아이콘 + 제목 */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 54, height: 54, borderRadius: 'var(--radius-lg)', background: 'var(--gt-subtle)', border: '1.5px solid color-mix(in srgb, var(--gt-primary) 25%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>

              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-0.4px', marginBottom: 8 }}>
                  중복 사진이 있습니다
                </div>
                <div style={{ fontSize: 13, color: 'var(--shell-on-surface-2)', lineHeight: 1.7 }}>
                  선택한 사진 중{' '}
                  <strong style={{ color: 'var(--gt-primary)' }}>{dupConfirm.matchedCount}장</strong>은 이미 업로드된 사진입니다.
                  {dupConfirm.unmatchedCount > 0 && (
                    <><br />새로 추가될 사진은{' '}<strong style={{ color: 'var(--color-success)' }}>{dupConfirm.unmatchedCount}장</strong>입니다.</>
                  )}
                </div>
                {dupConfirm.matchedSites.length > 0 && (
                  <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {dupConfirm.matchedSites.map(site => (
                      <span key={site} style={{ padding: '3px 10px', borderRadius: 'var(--radius-full)', fontSize: 12, fontWeight: 700, background: 'var(--gt-subtle)', color: 'var(--gt-primary)', border: '1px solid color-mix(in srgb, var(--gt-primary) 25%, transparent)' }}>
                        {site}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* 구분선 */}
            <div style={{ height: 1, background: 'var(--shell-border)' }} />

            {/* 중복 사진 썸네일 */}
            {dupConfirm.matchedFiles.length > 0 && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)' }}>
                    이미 업로드된 사진 {dupConfirm.matchedFiles.length}장
                  </div>
                  <div style={{ display: 'flex', gap: 8, ...(dupConfirm.matchedFiles.length > 4 ? { flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 4 } : { flexWrap: 'wrap' }) }}>
                    {dupConfirm.matchedFiles.map((f, i) => {
                      const url = fileThumbUrl(f);
                      return (
                        <div
                          key={i}
                          style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                            padding: 6, width: 75, boxSizing: 'border-box',
                            borderRadius: 'var(--radius-md)',
                            border: '1.5px solid var(--shell-border)',
                            background: 'var(--shell-surface)',
                            flexShrink: 0,
                          }}
                        >
                          <div style={{ width: 63, height: 63, borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--shell-skeleton)', flexShrink: 0 }}>
                            {url && <img src={url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt={f.name} />}
                          </div>
                          <span style={{ fontSize: 10.5, fontWeight: 400, color: 'var(--shell-on-surface-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 63 }}>
                            {f.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div style={{ height: 1, background: 'var(--shell-border)' }} />
              </>
            )}

            {/* 동일 사진(해시 중복) 그룹 — 덮어쓸 파일 선택 */}
            {dupConfirm.hashGroups.length > 0 && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {dupConfirm.hashGroups.map(g => (
                    <div key={g.hash} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)' }}>
                        동일한 사진 {g.files.length}장 중 덮어쓸 파일을 선택하세요
                        {g.siteName ? ` · ${g.siteName}` : ''}
                      </div>
                      <div style={{ display: 'flex', gap: 8, ...(g.files.length >= 4 ? { flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 4 } : { flexWrap: 'wrap' }) }}>
                        {g.files.map((f, fi) => {
                          const selected = (overwriteChoice[g.hash] ?? 0) === fi;
                          const url = fileThumbUrl(f);
                          return (
                            <button
                              key={fi}
                              onClick={() => setOverwriteChoice(prev => ({ ...prev, [g.hash]: fi }))}
                              style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                                padding: 6, width: 76, borderRadius: 'var(--radius-md)',
                                border: `1.5px solid ${selected ? 'var(--gt-primary)' : 'var(--shell-border)'}`,
                                background: selected ? 'var(--gt-subtle)' : 'var(--shell-surface)',
                                cursor: 'pointer', fontFamily: 'inherit',
                              }}
                            >
                              <div style={{ width: 56, height: 56, borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--shell-skeleton)', flexShrink: 0 }}>
                                {url && <img src={url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt={f.name} />}
                              </div>
                              <span style={{ fontSize: 10.5, fontWeight: selected ? 700 : 400, color: selected ? 'var(--gt-primary)' : 'var(--shell-on-surface-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 68 }}>
                                {f.name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ height: 1, background: 'var(--shell-border)' }} />
              </>
            )}

            {/* 액션 버튼 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {dupConfirm.unmatchedCount > 0 && (
                <Button
                  variant="success"
                  className="w-full"
                  onClick={() => { const f = dupConfirm.unmatchedFiles; closeDupConfirm(); void proceedUpload(f); }}
                >
                  중복 제외하고 업로드
                </Button>
              )}
              <Button
                variant="primary"
                className="w-full"
                onClick={() => {
                  const { hashGroups } = dupConfirm;
                  let finalFiles = rawFilesRef.current;
                  if (hashGroups.length) {
                    const groupFileSet = new Set(hashGroups.flatMap(g => g.files));
                    const chosen = hashGroups.map(g => g.files[overwriteChoice[g.hash] ?? 0]);
                    finalFiles = [...rawFilesRef.current.filter(f => !groupFileSet.has(f)), ...chosen];
                  }
                  closeDupConfirm();
                  void proceedUpload(finalFiles, true);
                }}
              >
                덮어쓰기
              </Button>
              <Button variant="etc" className="w-full" onClick={closeDupConfirm}>
                취소
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* D-07 §21.4 fixed — StepBar 를 고정하고 PageScroll 이 스크롤·폭·패딩을 갖는다 */}
      <Page layout="fixed">
        <StepBar step={1} subs={entryConfig?.stepSubs} />

        <PageScroll width="lg">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-lg)' }}>

            {/* 진입 동선 안내 배너 — entry 가 있을 때만 */}
            {entryConfig && (
              <Alert
                kind="info"
                title={entryConfig.banner.title}
                icon={entryConfig.banner.icon === 'info' ? undefined : CAMERA_ICON}
              >
                {entryConfig.banner.desc}
              </Alert>
            )}

            {/* 폰 갤러리 동선(MOBILE·CUSTOMER·PARTNER) — QR 카드 하나만 배치.
                드롭존·현장 미리 선택·업로드 예정 목록은 노출하지 않는다(사진을 PC로 받지 않으므로). */}
            {entryConfig?.qr ? (
              <QrHandoffPanel
                title={entryConfig.qr.title}
                subtitle={entryConfig.qr.subtitle}
                description={entryConfig.qr.description}
                descriptionStrong
                expiredLabel={entryConfig.qr.expiredLabel}
              />
            ) : entryConfig?.dropzone ? (
              /* entry 동선(PC) — 헤더 + 드롭 영역 + 선택 목록. 파일 0건이면 목록 영역은 접힌다. */
              <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-sm)' }}>
                {renderCardHeader()}
                {renderDropzone()}
                {files.length > 0 && renderThumbGrid()}
              </section>
            ) : (
            <>
            {/* entry 없는 진입(LNB "사진" 메뉴) — 드롭존(좌) + QR 핸드오프(우) 2단 현행 구성 */}
            <div style={{ display: 'flex', gap: 'var(--gap-lg)', alignItems: 'stretch' }}>
              {renderDropzone({ row: true })}

              {/* QR 완전 핸드오프(휴대폰으로 이어서 하기) — 우측 절반.
                  스캔하면 PC는 손을 뗀다 — 폰이 자기 계정으로 업로드·분류·태그까지 끝낸다. */}
              <QrHandoffPanel style={{ flex: 1 }} />
            </div>

            {/* 현장 미리 선택 블록 삭제 — 현장 배정은 GPS 자동분류 또는 분류 화면(2단계)에서 처리한다.
                selectedSite 상태·SiteSelectModal 배선은 그대로 남아 있으므로 필요해지면 트리거만 다시 붙이면 된다. */}

            {/* 업로드 예정 목록 */}
            <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-sm)' }}>
              {renderCardHeader()}
              {files.length === 0 ? (
                <EmptyState
                  icon={
                    /* Figma DS 2316-263 — 카메라 아이콘. 색은 .ico-chip 이 currentColor 로 준다 */
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M29 8H19L14 14H8C6.93913 14 5.92172 14.4214 5.17157 15.1716C4.42143 15.9217 4 16.9391 4 18V36C4 37.0609 4.42143 38.0783 5.17157 38.8284C5.92172 39.5786 6.93913 40 8 40H40C41.0609 40 42.0783 39.5786 42.8284 38.8284C43.5786 38.0783 44 37.0609 44 36V18C44 16.9391 43.5786 15.9217 42.8284 15.1716C42.0783 14.4214 41.0609 14 40 14H34L29 8Z" />
                      <path d="M24 32C27.3137 32 30 29.3137 30 26C30 22.6863 27.3137 20 24 20C20.6863 20 18 22.6863 18 26C18 29.3137 20.6863 32 24 32Z" />
                    </svg>
                  }
                  title="아직 선택한 사진이 없어요"
                  description="위 영역에 사진을 끌어다 놓거나 [파일 탐색기에서 선택]으로 고르세요"
                />
              ) : (
                renderThumbGrid()
              )}
            </section>
            </>
            )}

          </div>
        </PageScroll>
      </Page>

      {siteSelectOpen && (
        <SiteSelectModal
          onClose={() => setSiteSelectOpen(false)}
          onConfirm={handleSiteConfirm}
          onNewSiteRequest={() => { setSiteSelectOpen(false); setSiteRegOpen(true); }}
        />
      )}
      {siteRegOpen && (
        <SiteRegistrationPopup
          agentId={useAppStore.getState().subscriptions.find(s => s.agentType === 'A01')?.agentId ?? 1}
          onClose={() => setSiteRegOpen(false)}
          onComplete={(siteId: number) => void handleNewSiteComplete(siteId)}
        />
      )}
    </>
  );
}
